import { Injectable, inject } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { User } from '../_interfaces/user';

export type AnalyticsEvent =
  | 'user_logged_in'
  | 'consultation_started'
  | 'dictation_started'
  | 'start_dictation_clicked'
  | 'end_dictation_clicked'
  | 'audio_upload_started'
  | 'audio_upload_completed'
  | 'consultation_paused'
  | 'consultation_duration_limit_reached'
  | 'screen_wake_lock_unavailable'
  | 'encounter_resumed'
  | 'encounter_deleted'
  | 'context_note_deleted'
  | 'document_deleted'
  | 'priority_changed'
  | 'document_created'
  | 'document_manually_edited'
  | 'document_approved'
  | 'document_sent_to_editor'
  | 'patient_edited'
  | 'context_note_added'
  | 'sync_clicked'
  | 'sync_all_clicked'
  | 'regenerate_document_clicked'
  | 'regenerate_all_clicked'
  | 'regenerate_after_edit_clicked'
  | 'update_documents_clicked'
  | 'blueprint_created'
  | 'blueprints_page_viewed'
  | 'blueprint_opened'
  | 'style_settings_opened'
  | 'style_settings_saved'
  | 'style_setting_changed'
  | 'style_settings_cancelled'
  | 'schedule_viewed'
  | 'visit_selected'
  | 'mdr_page_viewed'
  | 'settings_page_viewed'
  | 'breadcrumb_clicked'
  | 'recipient_fields_disabled_viewed'
  | 'recipient_removed_from_field'
  | 'recipient_selector_opened'
  | 'recipient_tab_switched'
  | 'recipient_search_performed'
  | 'recipient_load_failed'
  | 'recipients_saved'
  | 'recipient_selector_dismissed'
  | 'free_text_recipient_added'
  | 'free_text_recipient_edited'
  | 'context_viewed'
  | 'transcript_viewed'
  | 'encounter_opened'
  | 'support_clicked'
  ;

export type PatientSelectEntryPoint =
  | 'schedule_home'
  | 'schedule_patient_select'
  | 'all_patient'
  | 'manual_patient'
  | 'no_patient';

export type SupportEntryPoint = 'nav_bar' | 'mobile_home_menu' | 'login';

export type AnalyticsEncounterType = 'consultation' | 'dictation' | 'audio_upload';

export type AnalyticsSurface =
  | 'consultation_screen'
  | 'document_screen'
  | 'encounters_list'
  | 'encounter'
  | 'document'
  | 'document_modal'
  | 'settings_page'
  | 'document_toolbar'
  | 'web_document';

export type StyleDetailLevel = 'brief' | 'balanced' | 'comprehensive';
export type StyleFormatMode = 'structured' | 'hybrid' | 'prose';
export type StyleAbbreviations = 'on' | 'off';
export type StyleQuotations = 'none' | 'salient_only' | 'liberal';
export type StyleEmptySections = 'leave_blank' | 'omit';
export type StyleClinicianReference = 'first_person' | 'passive' | 'third_person';
export type StylePatientReference = 'name_if_available_else_they' | 'name_only' | 'they';

export type StyleSettingField =
  | 'detail_level'
  | 'format_mode'
  | 'abbreviations'
  | 'quotations'
  | 'empty_sections'
  | 'refer_to_clinician'
  | 'refer_to_patient';

export type StyleSettingValue =
  | StyleDetailLevel
  | StyleFormatMode
  | StyleAbbreviations
  | StyleQuotations
  | StyleEmptySections
  | StyleClinicianReference
  | StylePatientReference;

export type ScheduleViewTrigger = 'day_navigation' | 'back_to_today' | 'back_from_all_patients';

export type DocumentCreatedTrigger = 'initial' | 'manual' | 'resume';

export type SettingsPageTab = 'my_details' | 'security' | 'blueprints_styles';

export type AnalyticsPage =
  | 'home'
  | 'encounters'
  | 'encounter'
  | 'document'
  | 'upload'
  | 'ai_blueprints'
  | 'ai_blueprint_editor';

export type RecipientSelectorEntryPoint = 'to_field' | 'cc_field';

export type RecipientAnalyticsType = 'primary' | 'cc';

export type RecipientAnalyticsSource = 'default' | 'address_book' | 'free_text';

export type RecipientAnalyticsTemplateType = 'organisation' | 'personal' | 'ai_blueprint';

export interface AnalyticsEventProps {
  encounter_id?: string;
  blueprint_id?: string;
  task_id?: number;
  entry_point?: PatientSelectEntryPoint | RecipientSelectorEntryPoint | SupportEntryPoint;
  status_before?: 'draft' | 'in_progress' | 'done';
  blueprint_count?: number;
  has_default_set?: boolean;
  event_source?: string;
  encounter_type?: AnalyticsEncounterType;
  surface?: AnalyticsSurface;
  trigger?: ScheduleViewTrigger | DocumentCreatedTrigger;
  day_offset?: number;
  visit_id?: string;
  list_position?: number;
  tab?: SettingsPageTab;
  from_page?: AnalyticsPage;
  to_page?: AnalyticsPage;
  changed_fields?: StyleSettingField[];
  changed_count?: number;
  field?: StyleSettingField;
  from?: StyleSettingValue;
  to?: StyleSettingValue;
  had_unsaved_changes?: boolean;
  detail_level_from?: StyleDetailLevel;
  detail_level_to?: StyleDetailLevel;
  format_mode_from?: StyleFormatMode;
  format_mode_to?: StyleFormatMode;
  abbreviations_from?: StyleAbbreviations;
  abbreviations_to?: StyleAbbreviations;
  quotations_from?: StyleQuotations;
  quotations_to?: StyleQuotations;
  empty_sections_from?: StyleEmptySections;
  empty_sections_to?: StyleEmptySections;
  refer_to_clinician_from?: StyleClinicianReference;
  refer_to_clinician_to?: StyleClinicianReference;
  refer_to_patient_from?: StylePatientReference;
  refer_to_patient_to?: StylePatientReference;
  was_approved?: boolean;
  was_suggested?: boolean;
  document_count?: number;
  recipient_type?: RecipientAnalyticsType;
  recipient_source?: RecipientAnalyticsSource;
  template_type?: RecipientAnalyticsTemplateType;
  has_existing_recipients?: boolean;
  to_tab?: RecipientAnalyticsType;
  result_count?: number;
  request_type?: 'defaults' | 'address_book_search';
  primary_changed?: boolean;
  cc_count?: number;
  added_count?: number;
  removed_count?: number;
  source_default_count?: number;
  source_address_book_count?: number;
  source_free_text_count?: number;
  char_count?: number;
  recipients_enabled?: boolean;
  has_primary?: boolean;
  free_text_count?: number;
  total_recorded_duration_seconds?: number;
  reason?: 'unsupported' | 'denied' | 'error';
}

/** Maps a stored session type to the analytics encounter_type token. */
export function toAnalyticsEncounterType(
  sessionType: string | null | undefined,
): AnalyticsEncounterType | undefined {
  switch (sessionType) {
    case 'encounter':
      return 'consultation';
    case 'dictation':
      return 'dictation';
    case 'upload':
      return 'audio_upload';
    default:
      return undefined;
  }
}

interface AnalyticsUserContext {
  userId?: string;
}

type AnalyticsPropertyName = keyof AnalyticsEventProps;
type AnalyticsPrimitive = string | number | boolean;
type AnalyticsPropertyValue = AnalyticsPrimitive | string[];
/** Safe event properties only — no PHI/PII (strict allowlist). */
const POSTHOG_PROPERTY_ALLOWLIST = [
  'entry_point',
  'status_before',
  'blueprint_id',
  'blueprint_count',
  'has_default_set',
  'encounter_id',
  'task_id',
  'event_source',
  'encounter_type',
  'surface',
  'trigger',
  'day_offset',
  'visit_id',
  'list_position',
  'tab',
  'from_page',
  'to_page',
  'changed_fields',
  'changed_count',
  'field',
  'from',
  'to',
  'had_unsaved_changes',
  'detail_level_from',
  'detail_level_to',
  'format_mode_from',
  'format_mode_to',
  'abbreviations_from',
  'abbreviations_to',
  'quotations_from',
  'quotations_to',
  'empty_sections_from',
  'empty_sections_to',
  'refer_to_clinician_from',
  'refer_to_clinician_to',
  'refer_to_patient_from',
  'refer_to_patient_to',
  'was_approved',
  'was_suggested',
  'document_count',
  'recipient_type',
  'recipient_source',
  'template_type',
  'has_existing_recipients',
  'to_tab',
  'result_count',
  'request_type',
  'primary_changed',
  'cc_count',
  'added_count',
  'removed_count',
  'source_default_count',
  'source_address_book_count',
  'source_free_text_count',
  'char_count',
  'recipients_enabled',
  'has_primary',
  'free_text_count',
  'total_recorded_duration_seconds',
  'reason',
] as const satisfies readonly AnalyticsPropertyName[];

const DEFAULT_HOST = 'https://eu.i.posthog.com';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly auth = inject(AuthService);

  private initialised = false;
  private effectsInitialised = false;

  private get enabled(): boolean {
    const posthogConfig = environment.analytics?.posthog;
    return !!posthogConfig?.enabled && !!posthogConfig.key?.trim();
  }

  /**
   * Bootstraps session tracking from the auth stream. Safe to call once via
   * `provideAppInitializer`; idempotent on repeated calls.
   */
  init(): void {
    if (this.effectsInitialised) return;
    this.effectsInitialised = true;
    this.trackSessions();
  }

  track(event: AnalyticsEvent, props: AnalyticsEventProps = {}): void {
    try {
      if (!this.enabled) return;
      this.ensureInit();
      if (!this.initialised) return;
      this.capture(event, props);
    } catch {
      console.warn('[analytics] track failed');
    }
  }

  /**
   * Maps the authentication stream to session events, preserving the reload
   * semantics of the previous inline implementation: the first emission for an
   * already-authenticated user (browser reload) resumes the session WITHOUT
   * re-emitting `user_logged_in`, so session counts are not inflated.
   */
  private trackSessions(): void {
    let previousUser: User | null = null;
    let isFirstEmission = true;

    this.auth.user$.subscribe(user => {
      try {
        if (isFirstEmission) {
          isFirstEmission = false;
          if (user) {
            this.resumeSession(this.toUserContext(user));
          }
          previousUser = user;
          return;
        }

        if (user && !previousUser) {
          this.startSession(this.toUserContext(user));
        } else if (!user && previousUser) {
          this.endSession();
        } else if (user && previousUser && user.id !== previousUser.id) {
          this.endSession();
          this.startSession(this.toUserContext(user));
        }
        previousUser = user;
      } catch {
        console.warn('[analytics] session update failed');
      }
    });
  }

  private startSession(user?: AnalyticsUserContext): void {
    try {
      if (!this.enabled) return;
      this.ensureInit();
      if (!this.initialised) return;
      this.identifyUser(user);
      this.capture('user_logged_in');
    } catch {
      console.warn('[analytics] startSession failed');
    }
  }

  private resumeSession(user?: AnalyticsUserContext): void {
    try {
      if (!this.enabled) return;
      this.ensureInit();
      if (!this.initialised) return;
      this.identifyUser(user);
    } catch {
      console.warn('[analytics] resumeSession failed');
    }
  }

  private endSession(): void {
    try {
      if (!this.initialised) return;
      posthog.reset();
    } catch {
      console.warn('[analytics] endSession failed');
    }
  }

  private ensureInit(): void {
    if (this.initialised || !this.enabled) return;

    const posthogConfig = environment.analytics?.posthog;
    const key = posthogConfig?.key?.trim();
    if (!key) return;

    try {
      posthog.init(key, {
        api_host: posthogConfig.host?.trim() || DEFAULT_HOST,
        autocapture: false,
        capture_pageview: 'history_change',
        capture_pageleave: true,
        disable_session_recording: true,
        capture_dead_clicks: false,
        capture_exceptions: false,
        person_profiles: 'identified_only',
        persistence: 'localStorage',
        enable_heatmaps: false,
        capture_heatmaps: false,
        enable_recording_console_log: false,
        disable_surveys: true,
        disable_external_dependency_loading: true,
        session_recording: { maskAllInputs: true, maskTextSelector: '*' },
      });
      this.initialised = true;
    } catch {
      console.warn('[analytics] init failed');
    }
  }

  private identifyUser(user?: AnalyticsUserContext): void {
    if (!user?.userId) return;
    try {
      posthog.identify(user.userId);
    } catch {
      console.warn('[analytics] identify failed');
    }
  }

  private capture(event: AnalyticsEvent, props: AnalyticsEventProps = {}): void {
    const safeProps = this.toSafeProps(props);
    posthog.capture(event, safeProps);
  }

  private toUserContext(user: User): AnalyticsUserContext {
    return {
      userId: user.id?.toString(),
    };
  }

  private toSafeProps(props: AnalyticsEventProps): Record<string, AnalyticsPropertyValue> {
    const result: Record<string, AnalyticsPropertyValue> = {};
    for (const key of POSTHOG_PROPERTY_ALLOWLIST) {
      const value = props[key];
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        result[key] = value;
        continue;
      }
      if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
        result[key] = [...value];
      }
    }
    return result;
  }
}
